import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ApartmentOutlined,
  CheckCircleOutlined,
  ClearOutlined,
  DeleteOutlined,
  EditOutlined,
  FileAddOutlined,
  SwapOutlined,
  WarningOutlined,
} from '@ant-design/icons';

import { MenuThaoTac, type MucThaoTac } from '../../../../components/shared';
import { Badge, Button, Card, CardBody, Checkbox, Input } from '../../../../components/ui';
import { useDuongDan } from '../../../../hooks/useDuongDan';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { soVN } from '../../../../lib/dinh-dang';
import { TRANG_THAI_NGUON } from '../../constants';
import { MO_TA_TOI_DA, PHUT_TOI_DA, PHUT_TOI_THIEU, TEN_PHUONG_NGU, docNhip } from '../../nghiep-vu/ket-noi';
import type { ChanDoanDsn, GraphqlSource } from '../../types';

export interface QuyenThe {
  sua: boolean;
  xoa: boolean;
}

/**
 * Một nguồn dữ liệu — DSN, GraphQL hay API, cùng một thẻ (port `TheNguon.tsx` native).
 *
 * Loại nguồn phải đọc được: GraphQL hỏng ở endpoint/khoá, DSN hỏng ở quyền tài khoản/lược đồ — người vận hành cần biết
 * mình đang nhìn cái nào TRƯỚC khi đi tìm nguyên nhân. Thẻ chỉ giữ hai việc hay làm; việc hiếm và việc phá huỷ nằm sau
 * menu tràn `⋮` (ADR-UI-0005), việc phá huỷ đứng cuối.
 *
 * Bỏ khối "phòng ban sở hữu và phạm vi" của native: platform không có trục phòng ban.
 */
export function TheNguon({
  s,
  chanDoan,
  quyen,
  dangKiem,
  dangDung,
  dangDongBo,
  dangKhaiNhanh,
  dangXoaKhoCu,
  onKiem,
  onDung,
  onDongBo,
  onDoiTuDongBo,
  onDoiNhip,
  onDoiMoTa,
  onSoDo,
  onXoa,
  onDoiCheDo,
  onSuaApi,
  onKhaiNhanh,
  onXoaKhoCu,
}: {
  s: GraphqlSource;
  chanDoan?: ChanDoanDsn;
  quyen: QuyenThe;
  dangKiem: boolean;
  dangDung: boolean;
  dangDongBo: boolean;
  dangKhaiNhanh: boolean;
  dangXoaKhoCu: boolean;
  onKiem: () => void;
  onDung: () => void;
  onDongBo: () => void;
  onDoiTuDongBo: () => void;
  /** Nhịp đồng bộ mới, tính bằng PHÚT. Bên gọi đổi ra giây. */
  onDoiNhip: (phut: number) => void;
  onDoiMoTa: (moTa: string) => void;
  onSoDo: () => void;
  onXoa: () => void;
  onDoiCheDo: () => void;
  onSuaApi: () => void;
  onKhaiNhanh: () => void;
  onXoaKhoCu: () => void;
}) {
  const { t, locale } = useT();
  const duongDan = useDuongDan();

  // Nhịp gõ tại chỗ, ghi khi rời ô: ghi theo từng phím thì gõ `120` bắn ba PATCH, và lời gọi đầu đặt nhịp thành 1 phút
  // — một giá trị hợp lệ, nên không gì chặn nó. Bản nháp `null` = đang hiện giá trị máy chủ.
  const phutHienTai = Math.round(s.syncIntervalSeconds / 60);
  const [nhipNhap, setNhipNhap] = useState<string | null>(null);
  const nhip = nhipNhap ?? String(phutHienTai);
  const ghiNhip = () => {
    const kq = docNhip(nhip, phutHienTai);
    if (kq.gui !== null) onDoiNhip(kq.gui);
    setNhipNhap(null);
  };

  const moTaHienTai = s.moTa ?? '';
  const [moTa, setMoTa] = useState<string | null>(null);
  const ghiMoTa = () => {
    if (moTa !== null && moTa.trim() !== moTaHienTai) onDoiMoTa(moTa.trim());
    setMoTa(null);
  };

  const trangThai = s.status in TRANG_THAI_NGUON ? s.status : 'draft';
  const laDsn = s.kind === 'dsn';
  const laApi = s.kind === 'api';
  // Nguồn API đọc thẳng mỗi lượt hỏi, không có bản sao — cùng nhánh với DSN trực tiếp ở mọi chỗ nói về đồng bộ.
  const trucTiep = laApi || (laDsn && s.accessMode === 'live');
  const theoKhuon = laApi || s.cheDo === 'khuon';
  const ten = (k: string) => TEN_PHUONG_NGU[k] ?? k;

  const tenLoai = laApi
    ? t('troLyAiKetNoiThe.loaiApi')
    : laDsn
      ? ten(s.dialect)
      : t('troLyAiKetNoiThe.loaiGraphql');
  const moTaCachDoc = laApi
    ? t('troLyAiKetNoiThe.cachDocApi')
    : trucTiep
      ? t('troLyAiKetNoiThe.cachDocTrucTiep')
      : s.autoSync
        ? t('troLyAiKetNoiThe.cachDocSaoChepTuDong', { n: soVN(phutHienTai, locale) })
        : t('troLyAiKetNoiThe.cachDocSaoChepTay');

  const muc: MucThaoTac[] = [
    laApi
      ? { khoa: 'suaApi', nhan: t('troLyAiKetNoiThe.suaKetNoiApi'), icon: <EditOutlined />, onChon: onSuaApi, hien: quyen.sua }
      : { khoa: 'soDo', nhan: t('troLyAiKetNoiThe.xemSoDo'), icon: <ApartmentOutlined />, onChon: onSoDo, nhom: 'xem' },
    {
      khoa: 'cheDo',
      nhan: trucTiep ? t('troLyAiKetNoiThe.chuyenSangSaoChep') : t('troLyAiKetNoiThe.chuyenSangTrucTiep'),
      icon: <SwapOutlined />,
      onChon: onDoiCheDo,
      hien: laDsn && quyen.sua,
    },
    // Khai nhanh ở đây vì đây là chỗ người ta VỪA nối xong một nguồn — nguồn có `0 thuật ngữ`. Việc làm một lần nên
    // đứng sau `⋮`, không cạnh "Kiểm tra kết nối".
    {
      khoa: 'khaiNhanh',
      nhan: t('troLyAiKetNoi.khaiNhanh'),
      icon: <FileAddOutlined />,
      onChon: onKhaiNhanh,
      voHieu: dangKhaiNhanh,
      hien: !laApi && quyen.sua,
    },
    {
      khoa: 'donKho',
      nhan: t('troLyAiKetNoi.xoaKhoCu'),
      icon: <ClearOutlined />,
      onChon: onXoaKhoCu,
      voHieu: dangXoaKhoCu,
      hien: trucTiep && Boolean(s.db.name) && quyen.xoa,
      nhom: 'pha-huy',
    },
    { khoa: 'xoa', nhan: t('troLyAiKetNoi.boNguon'), icon: <DeleteOutlined />, onChon: onXoa, hien: quyen.xoa, nhom: 'pha-huy' },
  ];

  return (
    <Card className="h-full">
      <CardBody className="flex h-full flex-col gap-3">
        <div className="flex min-w-0 items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold text-content-primary" title={s.name}>
              {s.name}
            </p>
            {moTa !== null ? (
              <Input
                autoFocus
                size="small"
                className="mt-1"
                maxLength={MO_TA_TOI_DA}
                value={moTa}
                placeholder={t('troLyAiKetNoiThe.moTaNguonGoiY')}
                aria-label={t('troLyAiKetNoiThe.moTaNguon')}
                onChange={(e) => setMoTa(e.target.value)}
                onBlur={ghiMoTa}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                  if (e.key === 'Escape') setMoTa(null);
                }}
              />
            ) : quyen.sua ? (
              <button
                type="button"
                title={t('troLyAiKetNoiThe.moTaNguonGhiChu')}
                onClick={() => setMoTa(moTaHienTai)}
                className={cn(
                  'mt-0.5 flex max-w-full items-center gap-1 truncate text-start text-xs',
                  moTaHienTai ? 'text-content-secondary hover:text-content-primary' : 'text-primary-700 hover:underline',
                )}
              >
                {moTaHienTai ? (
                  <>
                    <span className="truncate">{moTaHienTai}</span>
                    <EditOutlined aria-hidden className="shrink-0 text-content-muted" />
                  </>
                ) : (
                  t('troLyAiKetNoiThe.themMoTa')
                )}
              </button>
            ) : moTaHienTai ? (
              <p className="mt-0.5 truncate text-xs text-content-secondary">{moTaHienTai}</p>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Badge variant={TRANG_THAI_NGUON[trangThai].variant}>{t(`troLyAiKetNoiThe.trangThai.${trangThai}`)}</Badge>
            <MenuThaoTac muc={muc} tenBanGhi={s.name} />
          </div>
        </div>

        {/* Nguồn là gì, đọc thế nào — bằng lời, không bằng chuỗi kết nối. Nhãn phân loại, không phải trạng thái. */}
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <Badge variant="neutral">{tenLoai}</Badge>
          <Badge variant={trucTiep ? 'teal' : 'info'}>
            {trucTiep ? t('troLyAiKetNoiThe.nhanTrucTiep') : t('troLyAiKetNoiThe.nhanSaoChep')}
          </Badge>
          <Badge variant={theoKhuon ? 'violet' : 'orange'}>
            {theoKhuon ? t('troLyAiKetNoiThe.traLoiTheoKhuon') : t('troLyAiKetNoiThe.traLoiTheoBanKhai')}
          </Badge>
          <span className="text-content-muted">{moTaCachDoc}</span>
        </div>

        {s.lastError ? (
          <p role="alert" className="flex items-start gap-1.5 rounded bg-danger-50 px-2 py-1.5 text-xs text-danger-700">
            <WarningOutlined aria-hidden className="mt-0.5 shrink-0" />
            <span className="min-w-0 break-words">{s.lastError}</span>
          </p>
        ) : null}

        {chanDoan ? (
          <div className="space-y-1 rounded-lg bg-subtle p-2.5 text-xs text-content-secondary">
            <p>
              <b>{ten(chanDoan.phuongNgu)}</b> · {t('troLyAiKetNoiThe.luocDo')} <code>{chanDoan.luocDo}</code> ·{' '}
              {t('troLyAiKetNoiThe.bangDocDuoc', { n: soVN(chanDoan.soBang, locale) })}
              {chanDoan.soThuTuc ? (
                <> · {t('troLyAiKetNoiThe.thuTucGoiDuoc', { n: soVN(chanDoan.soThuTuc, locale) })}</>
              ) : null}
            </p>
            {/* Không có bảng nào đọc được là TRẠNG THÁI ĐÚNG của nguồn chỉ cho gọi thủ tục — nhưng phải nói chỗ cần chuyển. */}
            {chanDoan.chiThuTuc ? (
              <p
                className={cn(
                  'flex items-start gap-1.5',
                  chanDoan.cheDo === 'khuon' ? 'text-success-700' : 'text-warning-700',
                )}
              >
                {chanDoan.cheDo === 'khuon' ? (
                  <CheckCircleOutlined aria-hidden className="mt-0.5 shrink-0" />
                ) : (
                  <WarningOutlined aria-hidden className="mt-0.5 shrink-0" />
                )}
                <span>
                  {chanDoan.cheDo === 'khuon'
                    ? t('troLyAiKetNoiThe.chiThuTucSanSang')
                    : t('troLyAiKetNoiThe.chiThuTucCanChuyen')}
                </span>
              </p>
            ) : null}
            {chanDoan.phienBan ? <p className="text-content-muted">{chanDoan.phienBan}</p> : null}
            {/* Lớp bảo vệ phải ĐỌC ĐƯỢC: SQL Server không có công tắc chỉ-đọc, quyền tài khoản là lớp duy nhất. */}
            <p className={cn('flex items-start gap-1.5', chanDoan.chiDoc ? 'text-success-700' : 'text-warning-700')}>
              {chanDoan.chiDoc ? (
                <CheckCircleOutlined aria-hidden className="mt-0.5 shrink-0" />
              ) : (
                <WarningOutlined aria-hidden className="mt-0.5 shrink-0" />
              )}
              <span>{chanDoan.ghiChuChiDoc}</span>
            </p>
            {chanDoan.luocDoKhac.length > 1 ? (
              <p className="text-content-muted">
                {t('troLyAiKetNoiThe.luocDoKhac', { ds: chanDoan.luocDoKhac.slice(0, 8).join(', ') })}
              </p>
            ) : null}
          </div>
        ) : null}

        {/* Bản sao còn sót từ lúc chạy sao chép: không hỏng gì, chỉ chiếm chỗ — một dòng gợi ý nhỏ. */}
        {trucTiep && s.db.name ? <p className="text-xs text-content-muted">{t('troLyAiKetNoiThe.conKhoCu')}</p> : null}

        {trucTiep ? null : (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-content-secondary">
            <Checkbox
              checked={s.autoSync}
              disabled={s.status !== 'ready' || !quyen.sua}
              aria-label={t('troLyAiKetNoiThe.tuDongBo')}
              onChange={onDoiTuDongBo}
            >
              {s.autoSync ? t('troLyAiKetNoiThe.tuDongBoMoiTruoc') : t('troLyAiKetNoiThe.dongBoThuCong')}
            </Checkbox>
            {s.autoSync ? (
              <>
                <Input
                  size="small"
                  type="number"
                  min={PHUT_TOI_THIEU}
                  max={PHUT_TOI_DA}
                  disabled={s.status !== 'ready' || !quyen.sua}
                  className="w-[72px]"
                  aria-label={t('troLyAiKetNoiThe.nhipNhan')}
                  value={nhip}
                  onChange={(e) => setNhipNhap(e.target.value)}
                  onBlur={ghiNhip}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                    // Escape = bỏ sửa: không có đường lui thì con số gõ nhầm chỉ còn cách nhớ lại giá trị cũ.
                    if (e.key === 'Escape') setNhipNhap(null);
                  }}
                />
                <span>{t('troLyAiKetNoiThe.tuDongBoMoiSau')}</span>
              </>
            ) : null}
          </div>
        )}

        {/* Chi tiết kỹ thuật: có, nhưng gập lại — thứ quản trị CSDL cần khi dò lỗi. */}
        <details className="text-xs text-content-secondary">
          <summary className="cursor-pointer select-none text-content-muted hover:text-content-primary">
            {t('troLyAiKetNoiThe.chiTietKetNoi')}
          </summary>
          <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg bg-subtle p-2.5">
            <dt className="text-content-muted">{t('troLyAiKetNoiThe.ctDiaChi')}</dt>
            <dd className="truncate font-mono" title={s.endpoint}>
              {s.endpoint || '—'}
            </dd>
            {laApi ? (
              <>
                <dt className="text-content-muted">{t('troLyAiKetNoiThe.ctXacThuc')}</dt>
                <dd className="truncate font-mono">{s.api?.xacThuc ?? 'none'}</dd>
              </>
            ) : null}
            {laDsn ? (
              <>
                <dt className="text-content-muted">{t('troLyAiKetNoiThe.luocDo')}</dt>
                <dd className="truncate font-mono">{s.luocDo ?? '—'}</dd>
              </>
            ) : null}
            {s.db.name ? (
              <>
                <dt className="text-content-muted">{t('troLyAiKetNoiThe.ctKhoBanSao')}</dt>
                <dd className="truncate font-mono" title={s.db.name}>
                  {s.db.name}
                </dd>
                <dt className="text-content-muted">{t('troLyAiKetNoiThe.ctVaiDoc')}</dt>
                <dd className="truncate font-mono">{s.db.readerUser ?? '—'}</dd>
              </>
            ) : null}
          </dl>
        </details>

        {/* Hai việc hay làm, luôn ở cùng một chỗ. */}
        <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-default pt-3">
          {quyen.sua ? (
            <Button variant="secondary" size="sm" loading={dangKiem} onClick={onKiem}>
              {t('troLyAiKetNoiThe.kiemNoi')}
            </Button>
          ) : null}
          <Link
            to={duongDan(`${theoKhuon ? '/tro-ly-ai/khuon' : '/tro-ly-ai/ban-sao'}/${encodeURIComponent(s.id)}`)}
            className="text-xs text-primary-700 hover:underline"
          >
            {theoKhuon ? t('troLyAiKetNoiThe.moKhuon') : t('troLyAiKetNoiThe.moKhoDuLieu')}
          </Link>
          {trucTiep || !quyen.sua ? null : s.status !== 'ready' ? (
            <Button size="sm" loading={dangDung} onClick={onDung}>
              {t('troLyAiKetNoiThe.dungKho')}
            </Button>
          ) : (
            <Button size="sm" loading={dangDongBo} onClick={onDongBo}>
              {t('troLyAiKetNoiThe.dongBo')}
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
